import React, {
	useState,
	useEffect,
	CSSProperties,
	KeyboardEvent,
} from "react";
import { Slide } from "../slide";
import { Presentation, Theme, BroadcastMessage } from "../../lib/types";
import transitions from "../../lib/transitions";
import "./slideShow.scss";
import { useTransition, animated, TransitionKeyProps } from "react-spring";
import { Link, useHistory } from "react-router-dom";
import { ThemeStyle } from "../../ThemeStyle";
import { Share } from "../share";
import { getPresentUrl } from "../../lib/url-util";

export interface SlideShowProps {
	presentation: Presentation;
	slideIndex?: number;
	isControlsOpen?: boolean;
}

export const SlideShow = (props: SlideShowProps) => {
	const history = useHistory();

	const [slideIndex, setSlideIndex] = useState<number>(
		clampValue(props.slideIndex ?? 0, 1, props.presentation.slides.length) -
			1
	);
	const [isControlsOpen, setIsControlsOpen] = useState<boolean>(
		props.isControlsOpen ?? false
	);
	const [isClickEnabled, setIsClickEnabled] = useState<boolean>(false);
	const [isShareOpen, setIsShareOpen] = useState<boolean>(false);

	const [channelName] = useState<string>(
		`${props.presentation.title}-${generateUniqueId(5)}`
	);

	const [broadcastChannel] = useState<BroadcastChannel>(
		new BroadcastChannel(channelName)
	);

	const transitions = useTransition(
		slideIndex,
		(p) => p,
		Object(getTransition(props.presentation.theme))
	);

	useEffect(() => {
		// Only on first render, focus the slideshow
		(document.querySelector(".slide-show") as HTMLElement).focus();
		broadcastChannel.postMessage({
			notes: props.presentation.slides[slideIndex].notes,
			currentIndex: slideIndex,
			maxIndex: props.presentation.slides.length - 1,
		} as BroadcastMessage);

		// Link to notes window if open
		broadcastChannel.addEventListener("message", (message) => {
			let data = message.data as BroadcastMessage;
			setSlideIndex(data.currentIndex);
		});
	}, []);

	useEffect(() => {
		broadcastChannel.postMessage({
			notes: props.presentation.slides[slideIndex].notes,
			currentIndex: slideIndex,
			maxIndex: props.presentation.slides.length - 1,
		} as BroadcastMessage);
	}, [slideIndex]);

	useEffect(() => {
		if (window && window.history) {
			window.history.replaceState(
				null,
				`${props.presentation.title} ${slideIndex + 1}/${
					props.presentation.slides.length + 1
				}`,
				`${getPresentUrl(props.presentation.title)}/${slideIndex + 1}`
			);
		}
	}, [slideIndex]);

	const nextSlide = () => {
		setSlideIndex((prevIndex) =>
			Math.min(prevIndex + 1, props.presentation.slides.length - 1)
		);
	};

	const prevSlide = () => {
		setSlideIndex((prevIndex) => Math.max(prevIndex - 1, 0));
	};

	const initializeNotesLink = () => {
		broadcastChannel.postMessage({
			notes: props.presentation.slides[slideIndex].notes,
			currentIndex: slideIndex,
			maxIndex: props.presentation.slides.length - 1,
		} as BroadcastMessage);
	};

	const handleKeyPress = (e: KeyboardEvent) => {
		switch (e.key) {
			case "ArrowLeft":
				e.preventDefault();
				prevSlide();
				break;
			case "ArrowRight":
				e.preventDefault();
				nextSlide();
				break;
			case "ArrowDown":
				e.preventDefault();
				setIsControlsOpen(false);
				break;
			case "ArrowUp":
				e.preventDefault();
				setIsControlsOpen(true);
				break;
			case "s":
				e.preventDefault();
				setIsShareOpen(!isShareOpen);
				break;
			case "c":
				e.preventDefault();
				setIsClickEnabled(!isClickEnabled);
				break;
			case "n":
				e.preventDefault();
				window.open(`/notes/${channelName}`);
				setTimeout(() => {
					initializeNotesLink();
				}, 1000);
				break;
			case "h":
				e.preventDefault();
				history.push("/");
				break;
			case "Escape":
				e.preventDefault();
				if (document.fullscreen) {
					document.exitFullscreen();
				}
				break;
			case "F11":
				e.preventDefault();
				document.querySelector("body")?.requestFullscreen();
				break;
			default:
				break;
		}
	};
	return (
		<div
			className="slide-show"
			onKeyDown={(e) => handleKeyPress(e)}
			onClick={(e) => {
				if (isClickEnabled) {
					nextSlide();
				}
			}}
			style={props.presentation.style}
			tabIndex={0}
		>
			{transitions.map(({ item, props: styleProps, key }) => {
				const slide = props.presentation.slides[item];

				// Split content either horizontally or vertically
				let justifyStyle: CSSProperties = {};
				let justifyClass = "";
				switch (slide.justify) {
					case "-":
						justifyClass = "slide--split-horizontal";
						break;
					case "|":
						justifyClass = "slide--split-vertical";
						break;
					case "^-":
						justifyClass = "slide--split-horizontal-reverse";
						break;
					case "^|":
						justifyClass = "slide--split-vertical-reverse";
						break;
					case "v":
						justifyClass = "slide--centered";
						break;
					case "^":
						justifyClass = "slide--title-split-body";
						break;
				}

				return isShareOpen ? (
					<Share url={document.location.href} />
				) : (
					<animated.div
						style={{ ...styleProps, height: "100%" }}
						key={key}
					>
						<ThemeStyle theme={props.presentation.theme} />

						<Slide
							content={slide.content}
							style={{
								...props.presentation.style,
								...slide.style,
								...justifyStyle,
							}}
							className={`${justifyClass}`}
						/>
					</animated.div>
				);
			})}
			{isControlsOpen ? (
				<div
					className="slide-show__controls"
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					<div className="slide-show__group">
						<Link to={"/"} className="slide-show__button">
							<>
								<i
									className="material-icons"
									title="back to home"
								>
									home
								</i>
							</>
						</Link>

						<Link
							to={`/download/${props.presentation.title}`}
							className="slide-show__button"
							target="_blank"
							title="download slides"
						>
							<i className="material-icons">file_download</i>
						</Link>
						<button
							className={`slide-show__button ${
								isShareOpen ? "slide-show__button--active" : ""
							}`}
							title="share presentation"
							onClick={(e) => {
								setIsShareOpen((prev) => !prev);
							}}
						>
							<i className="material-icons">share</i>
						</button>
						<input
							type="checkbox"
							name="enableMouse"
							id="enableMouse"
							checked={isClickEnabled}
							className="slide-show__checkbox slide-show__checkbox--hidden"
							title="toggle click navigation"
							onChange={(value) => {
								setIsClickEnabled((prev) => !prev);
							}}
						/>
						<label
							className="slide-show__button"
							htmlFor="enableMouse"
							title="toggle click navigation"
						>
							<i className="material-icons">mouse</i>
						</label>
					</div>

					<div className="slide-show__group">
						<button
							className="slide-show__button slide-show__button--show-hide"
							title="hide controls"
							onClick={(e) => {
								e.stopPropagation();
								setIsControlsOpen(false);
							}}
						>
							<i className="material-icons">expand_more</i>
						</button>
					</div>

					<div className="slide-show__group">
						<button
							className="slide-show__button"
							onClick={(e) => {
								e.stopPropagation();
								prevSlide();
							}}
						>
							<i
								className="material-icons"
								title="previous slide"
							>
								chevron_left
							</i>
						</button>
						<button
							className="slide-show__button "
							onClick={(e) => {
								e.stopPropagation();
								nextSlide();
							}}
						>
							<i className="material-icons" title="next slide">
								chevron_right
							</i>
						</button>
						<Link
							to={`/notes/${channelName}`}
							className="slide-show__button"
							target="_blank"
							title="speaker notes"
							onClick={() =>
								setTimeout(() => {
									initializeNotesLink();
								}, 1000)
							}
						>
							<i className="material-icons">speaker_notes</i>
						</Link>
						<div className="slide-show__text">
							{slideIndex + 1}/{props.presentation.slides.length}
						</div>
					</div>
				</div>
			) : (
				<div
					className="slide-show__controls slide-show__controls--collapsed"
					onClick={(e) => {
						e.stopPropagation();
						setIsControlsOpen(true);
					}}
				>
					<button
						className="slide-show__button"
						title="expand controls"
					>
						<i className="material-icons">expand_less</i>
					</button>
				</div>
			)}
		</div>
	);
};

const getTransition = (theme: Theme | undefined) => {
	const defaultTransition = {
		from: { opacity: 0 },
		enter: { opacity: 1 },
		leave: {
			display: "none",
		},
	};
	if (!theme || !theme.transition) {
		return defaultTransition;
	}
	switch (theme.transition) {
		case "fade":
			return transitions.fade;
			break;
		case "swipeLeft":
			return transitions.swipeLeft;
			break;
		case "swipeDown":
			return transitions.swipeDown;
		case "zoom":
			return transitions.zoom;
		default:
			return defaultTransition;
			break;
	}
};

const clampValue = (value: number, min: number, max: number) => {
	return Math.min(Math.max(min, value || min), Math.min(max, value || min));
};

const generateUniqueId = (length: number): string => {
	return [...Array(length).keys()]
		.map((i) => {
			return String.fromCharCode(Math.floor(Math.random() * 25 + 97));
		})
		.join("");
};
